import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import IosShareIcon from '@mui/icons-material/IosShare';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { EvidenceRef, formatDate, LearnedEntry, LearningSettings, MappingItem, TargetDetail, TargetType } from '../../services/learningApi';
import { EntryStatusChip, HelpTip, RunLink, useApis } from './common';
import { KnowledgeBaseCard } from './KnowledgeBaseCard';
import { ExportKnowledgeDialog } from './ExportKnowledgeDialog';
import { ImportKnowledgeDialog } from './ImportKnowledgeDialog';
import { KnowledgeLifecycle } from './KnowledgeLifecycle';

interface Props {
  targetType: TargetType;
  targetId: string;
  detail: TargetDetail;
  onChanged: () => void;
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'active,pinned', label: 'In use' },
  { value: 'candidate', label: 'Waiting' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'stale', label: 'Stale' },
];

/** Evidence strength = s / (s + 2), where s is the weighted run support; see consolidate.entry_confidence. */
function runsStillNeeded(e: LearnedEntry, k: LearningSettings['knowledge']): number {
  const support =
    e.kind === 'fact'
      ? Math.max(0, e.evidence_positive - e.evidence_negative)
      : Math.max(0, e.evidence_positive) + Math.max(0, e.evidence_negative);
  const c = Math.min(k.min_confidence, 0.99);
  const forStrength = Math.max(0, Math.ceil((2 * c) / (1 - c) - support - 1e-9));
  const forCount = Math.max(0, k.min_evidence - e.evidence_count);
  return Math.max(forStrength, forCount);
}

const STRENGTH_HELP =
  'How much evidence backs this entry, not whether it is right: 1 run ≈ 33%, 2 ≈ 50%, 3 ≈ 60%, 8 ≈ 80%. ' +
  'Runs the AI graded as poor count for less; runs with positive feedback count for more. ' +
  'For facts, runs that contradict it cancel out runs that confirm it.';

const net = (it: MappingItem) => it.positive - it.negative;

const verdictOf = (it: MappingItem): { label: string; color: 'success' | 'error' | 'default' } =>
  net(it) > 0 ? { label: 'Worked', color: 'success' } : net(it) < 0 ? { label: 'Avoid', color: 'error' } : { label: 'No verdict', color: 'default' };

/** Brand/other identifying fields the analyzer kept, e.g. "4505 Meats". */
const detailText = (it: MappingItem) =>
  Object.entries(it.details || {})
    .filter(([k, v]) => k !== 'id' && v !== null && v !== '' && typeof v !== 'object')
    .map(([k, v]) => (k === 'brand' ? String(v) : `${k}: ${v}`))
    .join(' · ');

const MappingSummary: React.FC<{ items: MappingItem[] }> = ({ items }) => {
  const sorted = [...items].sort((a, b) => net(b) - net(a));
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {sorted.slice(0, 4).map((it) => {
        const v = verdictOf(it);
        const extra = detailText(it);
        return (
          <Tooltip
            key={it.label}
            title={`${v.label}: ${it.positive.toFixed(1)} for · ${it.negative.toFixed(1)} against${it.notes?.[0] ? ` — ${it.notes[0]}` : ''}`}
          >
            <Chip size="small" label={extra ? `${it.label} · ${extra}` : it.label} color={v.color} variant="outlined" />
          </Tooltip>
        );
      })}
      {sorted.length > 4 && <Chip size="small" label={`+${sorted.length - 4} more`} />}
    </Stack>
  );
};

/** Expanded row: every result with its counts and reasons, and the exact text runs receive. */
const EntryDetail: React.FC<{ entry: LearnedEntry; itemLabel: string }> = ({ entry, itemLabel }) => {
  const items = [...(entry.content.items || [])].sort((a, b) => net(b) - net(a));
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ py: 1.5 }}>
      <Box sx={{ flex: 3, minWidth: 0 }}>
        {entry.kind === 'mapping' ? (
          <>
            <Typography variant="subtitle2" gutterBottom>
              {itemLabel}s recorded for this input
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Result</TableCell>
                  <TableCell>Verdict</TableCell>
                  <TableCell align="right">
                    For / against
                    <HelpTip title="Weighted run support. Runs with positive feedback count more; runs the analyzer graded mixed or poor count less." />
                  </TableCell>
                  <TableCell>Why (analyzer notes)</TableCell>
                  <TableCell>Last seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => {
                  const v = verdictOf(it);
                  return (
                    <TableRow key={it.label}>
                      <TableCell>
                        <Typography variant="body2">{it.label}</Typography>
                        {detailText(it) && (
                          <Typography variant="caption" color="text.secondary">
                            {detailText(it)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={v.label} color={v.color} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        {it.positive.toFixed(1)} / {it.negative.toFixed(1)}
                        {it.neutral > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            +{it.neutral} seen without a verdict
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        {(it.notes || []).map((n, i) => (
                          <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                            {n}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(it.last_seen || null)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Only results marked Worked or Avoid are sent to runs. No-verdict results are kept as evidence until runs show whether they work.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Fact
            </Typography>
            <Typography variant="body2">{entry.content.text || entry.key}</Typography>
          </>
        )}
      </Box>
      <Box sx={{ flex: 2, minWidth: 0 }}>
        <Typography variant="subtitle2" gutterBottom>
          {entry.in_use ? 'Sent to matching runs as' : 'Will be sent as (once in use)'}
        </Typography>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
          <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {entry.sent_text || '—'}
          </Typography>
        </Paper>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {entry.in_use
            ? 'A run gets this text when its lookup contains the input name or one of its aliases, or is close in meaning. It also appears in the published knowledge document.'
            : entry.status === 'candidate'
              ? 'Waiting: not sent to runs or published yet.'
              : `Not sent to runs (status: ${entry.status}).`}
        </Typography>
      </Box>
    </Stack>
  );
};

export const KnowledgeTab: React.FC<Props> = ({ targetType, targetId, detail, onChanged }) => {
  const apis = useApis();
  const [entries, setEntries] = useState<LearnedEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [kind, setKind] = useState('');
  const [sort, setSort] = useState('evidence');
  const [orphanedOnly, setOrphanedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkAnchor, setBulkAnchor] = useState<HTMLElement | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [editing, setEditing] = useState<LearnedEntry | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const keyLabel = detail.profile?.observation?.key_label || 'Input';
  const itemLabel = detail.profile?.observation?.item_label || 'Result';
  const [evidenceFor, setEvidenceFor] = useState<LearnedEntry | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportFor, setExportFor] = useState<{ ids: string[] | null; count: number; notInUse: number } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const knowledgeSettings = detail.settings.knowledge;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => setPage(0), [debouncedQ, statusFilter, kind, sort, orphanedOnly]);

  const hasConfig = !!detail.config;
  const load = useCallback(async () => {
    if (!hasConfig) return;
    setLoading(true);
    try {
      const res = await apis.learning.entries(targetType, targetId, {
        status: statusFilter || undefined,
        kind: kind || undefined,
        q: debouncedQ || undefined,
        orphaned: orphanedOnly ? true : undefined,
        sort,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setEntries(res.entries);
      setTotal(res.total);
      setSelected((sel) => sel.filter((id) => res.entries.some((e) => e.id === id)));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apis, targetType, targetId, hasConfig, statusFilter, kind, debouncedQ, orphanedOnly, sort, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  if (!detail.config) {
    return <Alert severity="info">Nothing learned yet. Turn self-improvement on and run an analysis to start building knowledge.</Alert>;
  }

  const afterChange = async (msg?: string) => {
    if (msg) setInfo(msg);
    await load();
    setRefreshKey((k) => k + 1);
    onChanged();
  };

  const act = async (fn: () => Promise<unknown>, msg?: string) => {
    setError(null);
    try {
      await fn();
      await afterChange(msg);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const setStatus = (e: LearnedEntry, status: LearnedEntry['status']) =>
    act(
      () => apis.learning.updateEntry(e.id, { status }),
      status === 'active' && e.status === 'candidate' ? 'Approved — it is now in use and will be published in a few seconds.' : undefined,
    );

  const bulkSetStatus = (ids: string[], status: LearnedEntry['status'], msg: string) =>
    act(async () => {
      await apis.learning.bulkStatus(targetType, targetId, ids, status);
      setSelected([]);
    }, msg);

  const approveAllWaiting = () =>
    setConfirm({
      title: 'Approve all waiting learnings?',
      body: 'Every waiting entry goes live and will be given to runs. Only do this after reading them; you can still reject any of them later.',
      confirmLabel: 'Approve all',
      run: async () => {
        const ids: string[] = [];
        for (let offset = 0; ; offset += 200) {
          const res = await apis.learning.entries(targetType, targetId, { status: 'candidate', limit: 200, offset });
          ids.push(...res.entries.map((e) => e.id));
          if (res.entries.length < 200) break;
        }
        if (ids.length) await apis.learning.bulkStatus(targetType, targetId, ids, 'active');
        await afterChange(`Approved ${ids.length} entr${ids.length === 1 ? 'y' : 'ies'}.`);
      },
    });

  const askBulk = (
    title: string,
    body: string,
    payload: { ids?: string[]; orphaned?: boolean; status?: string; all?: boolean; rescan?: boolean },
  ) => {
    setBulkAnchor(null);
    setConfirm({
      title,
      body,
      run: async () => {
        const res = await apis.learning.bulkDeleteEntries(targetType, targetId, payload);
        setSelected([]);
        const rescan = res.runs_forgotten ? ` The next analysis will read ${res.runs_forgotten} past runs again.` : '';
        await afterChange(`Deleted ${res.deleted} entr${res.deleted === 1 ? 'y' : 'ies'}.${rescan}`);
      },
    });
  };

  const counts = detail.config.entry_counts || {};
  const allOnPageSelected = entries.length > 0 && entries.every((e) => selected.includes(e.id));

  return (
    <Box>
      <KnowledgeLifecycle settings={detail.settings} categoryName={detail.knowledge?.category_name ?? null} />
      <KnowledgeBaseCard
        targetType={targetType}
        targetId={targetId}
        refreshKey={refreshKey}
        onShowStatus={setStatusFilter}
        onApproveAllWaiting={approveAllWaiting}
        onExportAll={(count) => setExportFor({ ids: null, count, notInUse: 0 })}
        onImport={() => setImportOpen(true)}
      />
      {importOpen && (
        <ImportKnowledgeDialog
          targetType={targetType}
          targetId={targetId}
          configId={detail.config.id}
          onClose={() => setImportOpen(false)}
          onImported={(msg) => {
            setImportOpen(false);
            afterChange(msg);
          }}
        />
      )}
      {exportFor && (
        <ExportKnowledgeDialog
          targetType={targetType}
          targetId={targetId}
          targetName={detail.target.name}
          entryIds={exportFor.ids}
          count={exportFor.count}
          notInUseCount={exportFor.notInUse}
          learnedCategoryId={detail.config.knowledge_category_id}
          onClose={() => setExportFor(null)}
        />
      )}

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        Learned entries
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each row is something the analyzer noticed across your runs: a <b>mapping</b> (an input and the results that worked or didn’t for it) or
        a <b>fact</b> (a general rule). Only rows marked “In use” are given to runs.{' '}
        {detail.knowledge?.category_name ? (
          <>
            All of them publish to one knowledge category, <b>{detail.knowledge.category_name}</b>, so there’s no category column. The small chip
            under a name is the analyzer’s topic label (for grouping and search), not a Knowledge Base category.
          </>
        ) : (
          'All of them publish to this ' + (detail.target.type === 'chain' ? 'chain' : 'function') + '’s one learned knowledge category.'
        )}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          placeholder="Search names or topics"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 240 }}
        />
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 180 }}>
          {STATUS_FILTERS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Type" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="mapping">Mappings</MenuItem>
          <MenuItem value="fact">Facts</MenuItem>
        </TextField>
        <TextField select size="small" label="Sort" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="evidence">Most evidence</MenuItem>
          <MenuItem value="recent">Recently seen</MenuItem>
          <MenuItem value="confidence">Confidence</MenuItem>
          <MenuItem value="key">A–Z</MenuItem>
        </TextField>
        <Tooltip title="Entries whose supporting runs were all deleted by data retention. The learning still works; you just can't inspect where it came from.">
          <FormControlLabel control={<Switch size="small" checked={orphanedOnly} onChange={(e) => setOrphanedOnly(e.target.checked)} />} label="No run history" />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {selected.length > 0 && (
          <>
            <Button color="success" startIcon={<CheckCircleOutlineIcon />} onClick={() => bulkSetStatus(selected, 'active', `Approved ${selected.length} — now in use.`)}>
              Approve {selected.length}
            </Button>
            <Button startIcon={<BlockIcon />} onClick={() => bulkSetStatus(selected, 'rejected', `Rejected ${selected.length}.`)}>
              Reject {selected.length}
            </Button>
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => askBulk('Delete selected entries?', `${selected.length} entries will be removed permanently.`, { ids: selected })}>
              Delete {selected.length}
            </Button>
            <Tooltip title="Copy the selected entries into a separate, frozen Knowledge Base file in a category you choose">
              <Button
                startIcon={<IosShareIcon />}
                onClick={() =>
                  setExportFor({
                    ids: selected,
                    count: selected.length,
                    notInUse: entries.filter((e) => selected.includes(e.id) && e.status !== 'active' && e.status !== 'pinned').length,
                  })
                }
              >
                Export {selected.length}
              </Button>
            </Tooltip>
          </>
        )}
        <IconButton onClick={(e) => setBulkAnchor(e.currentTarget)} aria-label="More actions">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={bulkAnchor} open={!!bulkAnchor} onClose={() => setBulkAnchor(null)}>
          <MenuItem
            onClick={() =>
              askBulk('Delete entries without run history?', 'Every entry whose supporting runs have all been purged will be removed.', { orphaned: true })
            }
          >
            Delete all without run history
          </MenuItem>
          <MenuItem
            disabled={!counts.rejected}
            onClick={() =>
              askBulk(
                'Delete rejected entries?',
                'Rejected entries are kept so the analyzer does not learn them again. Deleting them means they may come back.',
                { status: 'rejected' },
              )
            }
          >
            Delete all rejected ({counts.rejected || 0})
          </MenuItem>
          <MenuItem disabled={!counts.stale} onClick={() => askBulk('Delete stale entries?', 'Entries not seen recently will be removed.', { status: 'stale' })}>
            Delete all stale ({counts.stale || 0})
          </MenuItem>
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() =>
              askBulk(
                'Start over?',
                'Every entry is deleted, including pinned and rejected ones, and past runs are marked as not analyzed. The next analysis rebuilds the knowledge from runs still in its window. Use this after changing the profile or learn nodes.',
                { all: true, rescan: true },
              )
            }
          >
            Start over (delete all and re-analyze)
          </MenuItem>
        </Menu>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Paper variant="outlined">
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allOnPageSelected}
                    indeterminate={!allOnPageSelected && entries.some((e) => selected.includes(e.id))}
                    onChange={(e) => setSelected(e.target.checked ? entries.map((x) => x.id) : [])}
                  />
                </TableCell>
                <TableCell>
                  Input
                  <HelpTip title={`The ${keyLabel} this entry is about (or a fact's topic). Runs whose lookup contains this name or an alias get the entry.`} />
                </TableCell>
                <TableCell>
                  What it learned
                  <HelpTip
                    title={`${itemLabel}s seen for this input. Green = worked, red = avoid, grey = seen without a verdict (kept as evidence, not sent). Expand the row for every result, the reasons, and the exact text runs receive.`}
                  />
                </TableCell>
                <TableCell align="right">
                  Runs
                  <HelpTip title="How many analyzed runs support this entry. Click the list icon to open them." />
                </TableCell>
                <TableCell>
                  Evidence strength
                  <HelpTip title={STRENGTH_HELP} />
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      {total === 0 && !debouncedQ && !statusFilter && !kind && !orphanedOnly
                        ? 'Nothing learned yet. Run an analysis from the Overview tab, or import knowledge from another function or chain (Import… above).'
                        : 'No entries match these filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {entries.map((e) => (
                <React.Fragment key={e.id}>
                <TableRow hover selected={selected.includes(e.id)} sx={{ '& > td': { borderBottom: expanded === e.id ? 'unset' : undefined } }}>
                  <TableCell padding="checkbox">
                    <IconButton size="small" aria-label="Show details" onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                      {expanded === e.id ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.includes(e.id)}
                      onChange={(ev) => setSelected((s) => (ev.target.checked ? [...s, e.id] : s.filter((x) => x !== e.id)))}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {e.kind === 'fact' ? `Fact: ${e.key}` : e.key}
                    </Typography>
                    {e.aliases.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Also matches: {e.aliases.join(', ')}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {e.category && (
                        <Tooltip title="Topic label from the analyzer. Used for grouping and search; it isn’t a Knowledge Base category.">
                          <Chip size="small" label={e.category} variant="outlined" />
                        </Tooltip>
                      )}
                      {e.human_edited && <Chip size="small" label="edited" />}
                      {e.origin && (
                        <Tooltip
                          title={`Copied from the ${e.origin.target_type} “${e.origin.target_name}” on ${formatDate(e.origin.imported_at)}, where ${e.origin.evidence_count} run${e.origin.evidence_count === 1 ? '' : 's'} supported it. Runs and evidence strength here count only this ${targetType}'s runs.`}
                        >
                          <Chip size="small" variant="outlined" color="info" label="imported" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    {e.kind === 'mapping' ? (
                      <MappingSummary items={e.content.items || []} />
                    ) : (
                      <Typography variant="body2">{e.content.text || e.key}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={`${e.evidence_positive.toFixed(1)} positive · ${e.evidence_negative.toFixed(1)} negative from ${e.evidence_count} runs`}>
                      <span>
                        {e.evidence_count}
                        {e.orphaned && (
                          <Tooltip title="All supporting runs have been purged by data retention">
                            <LinkOffIcon fontSize="inherit" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                          </Tooltip>
                        )}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ minWidth: 110 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round(e.confidence * 100)}
                      color={e.confidence >= knowledgeSettings.min_confidence ? 'success' : 'primary'}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(e.confidence * 100)}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <EntryStatusChip status={e.status} />
                    {e.status === 'candidate' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {!knowledgeSettings.auto_publish
                          ? 'Needs your approval'
                          : runsStillNeeded(e, knowledgeSettings) > 0
                            ? `Live after ~${runsStillNeeded(e, knowledgeSettings)} more run${runsStillNeeded(e, knowledgeSettings) === 1 ? '' : 's'}`
                            : 'Goes live next analysis'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(e.last_seen_at)}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {e.status === 'candidate' && (
                      <Tooltip title="Use it now, without waiting for more runs">
                        <Button size="small" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={() => setStatus(e, 'active')} sx={{ mr: 0.5 }}>
                          Approve
                        </Button>
                      </Tooltip>
                    )}
                    {e.status === 'pinned' ? (
                      <Tooltip title="Unpin">
                        <IconButton size="small" onClick={() => setStatus(e, 'active')}>
                          <PushPinIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Pin (always use, never auto-stale)">
                        <IconButton size="small" onClick={() => setStatus(e, 'pinned')}>
                          <PushPinOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {e.status === 'rejected' ? (
                      <Tooltip title="Restore">
                        <IconButton size="small" onClick={() => setStatus(e, 'active')}>
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reject (stop using and don't re-learn)">
                        <IconButton size="small" onClick={() => setStatus(e, 'rejected')}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Supporting runs">
                      <IconButton size="small" onClick={() => setEvidenceFor(e)}>
                        <ListAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing(e)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setConfirm({
                            title: 'Delete this entry?',
                            body: 'It may be learned again by a future cycle. Reject it instead to keep it out for good.',
                            run: () => act(() => apis.learning.deleteEntry(e.id), 'Entry deleted.'),
                          })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 0, ...(expanded === e.id ? {} : { borderBottom: 'none' }) }}>
                    <Collapse in={expanded === e.id} timeout="auto" unmountOnExit>
                      <EntryDetail entry={e} itemLabel={itemLabel} />
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} onError={setError} />
      {editing && (
        <EditEntryDialog
          entry={editing}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            await apis.learning.updateEntry(editing.id, body);
            setEditing(null);
            await afterChange('Entry updated.');
          }}
        />
      )}
      {evidenceFor && <EvidenceDialog entry={evidenceFor} onClose={() => setEvidenceFor(null)} />}
    </Box>
  );
};

interface ConfirmState {
  title: string;
  body: string;
  run: () => Promise<void>;
  /** Defaults to a red "Delete" button. */
  confirmLabel?: string;
}

const ConfirmDialog: React.FC<{
  confirm: ConfirmState | null;
  onClose: () => void;
  onError: (msg: string) => void;
}> = ({ confirm, onClose, onError }) => {
  const [busy, setBusy] = useState(false);
  if (!confirm) return null;
  return (
    <Dialog open onClose={() => !busy && onClose()}>
      <DialogTitle>{confirm.title}</DialogTitle>
      <DialogContent>
        <Typography>{confirm.body}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color={confirm.confirmLabel ? 'primary' : 'error'}
          variant="contained"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await confirm.run();
              onClose();
            } catch (e: any) {
              onError(e.message);
              onClose();
            } finally {
              setBusy(false);
            }
          }}
        >
          {confirm.confirmLabel || 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditEntryDialog: React.FC<{
  entry: LearnedEntry;
  onClose: () => void;
  onSave: (body: { key?: string; category?: string; content?: LearnedEntry['content'] }) => Promise<void>;
}> = ({ entry, onClose, onSave }) => {
  const [key, setKey] = useState(entry.key);
  const [category, setCategory] = useState(entry.category || '');
  const [text, setText] = useState(entry.content.text || '');
  const [items, setItems] = useState<MappingItem[]>(entry.content.items || []);
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({
        key: entry.kind === 'mapping' ? key : undefined,
        category,
        content: entry.kind === 'fact' ? { text } : { items },
      });
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {entry.kind === 'fact' ? 'fact' : 'mapping'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          {entry.kind === 'mapping' && <TextField label="Key" value={key} onChange={(e) => setKey(e.target.value)} fullWidth />}
          <TextField
            label="Topic"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
            helperText="A label for grouping and search, e.g. “dairy”. Not a Knowledge Base category."
          />
          {entry.kind === 'fact' ? (
            <TextField label="Fact" multiline minRows={3} value={text} onChange={(e) => setText(e.target.value)} fullWidth />
          ) : (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Items
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Remove items that are wrong, or add ones you know work. Evidence counts are kept.
              </Typography>
              <List dense disablePadding>
                {items.map((it, i) => (
                  <ListItem
                    key={i}
                    disableGutters
                    secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => setItems(items.filter((_, j) => j !== i))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <TextField
                      size="small"
                      value={it.label}
                      onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                      sx={{ flex: 1, mr: 5 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" color="text.secondary">
                              +{it.positive.toFixed(0)} / −{it.negative.toFixed(0)}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField size="small" placeholder="Add an item" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} sx={{ flex: 1 }} />
                <Button
                  startIcon={<AddIcon />}
                  disabled={!newLabel.trim()}
                  onClick={() => {
                    setItems([...items, { label: newLabel.trim(), positive: 1, negative: 0, neutral: 0, notes: ['Added manually'] }]);
                    setNewLabel('');
                  }}
                >
                  Add
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EvidenceDialog: React.FC<{ entry: LearnedEntry; onClose: () => void }> = ({ entry, onClose }) => {
  const apis = useApis();
  const [refs, setRefs] = useState<EvidenceRef[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    apis.learning
      .evidence(entry.id)
      .then((r) => setRefs(r.refs))
      .catch((e) => setError(e.message));
  }, [apis, entry.id]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Supporting runs — {entry.kind === 'fact' ? 'fact' : entry.key}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!refs && !error && <CircularProgress size={24} />}
        {refs && refs.length === 0 && <Typography color="text.secondary">No run references recorded.</Typography>}
        {refs && refs.length > 0 && (
          <List dense>
            {refs.map((r) => {
              const learned = r.item || r.text;
              return (
                <ListItem
                  key={r.id}
                  disableGutters
                  divider
                  alignItems="flex-start"
                  secondaryAction={<RunLink type={r.type} id={r.id} available={r.history_available} label="Open run" />}
                >
                  <ListItemText
                    sx={{ pr: 12 }}
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Chip
                          size="small"
                          label={r.polarity === 'positive' ? 'Worked' : r.polarity === 'negative' ? 'Did not work' : 'No verdict'}
                          color={r.polarity === 'positive' ? 'success' : r.polarity === 'negative' ? 'error' : 'default'}
                          variant="outlined"
                        />
                        <Typography variant="body2">{r.type === 'chain_execution' ? 'Chain run' : 'Function run'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(r.at)}
                        </Typography>
                        {r.run_quality && (
                          <Tooltip title={r.run_quality_reason || ''}>
                            <Chip
                              size="small"
                              label={`Run graded ${r.run_quality}`}
                              color={r.run_quality === 'good' ? 'success' : r.run_quality === 'bad' ? 'error' : 'warning'}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        {r.input_preview && (
                          <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                            <b>Input:</b> {r.input_preview}
                          </Typography>
                        )}
                        {learned ? (
                          <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                            <b>{entry.kind === 'fact' ? 'Fact' : 'Result'}:</b> {learned}
                          </Typography>
                        ) : (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Recorded before per-run details were kept; open the run to see it.
                          </Typography>
                        )}
                        {r.note && (
                          <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                            <b>Why:</b> {r.note}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
        {entry.evidence_count > (refs?.length || 0) && refs && (
          <Typography variant="caption" color="text.secondary">
            Showing the {refs.length} most recent of {entry.evidence_count} supporting runs.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
